import { render, useEffect } from 'preact/compat';
import { useState } from 'preact/hooks';
import { CommentsService } from '@ts/services/commentsService';
import { isCommentAllowed, afterLoadingComments, allowedTime } from './comment';
import { commentForm } from './form';
import { CommentModal } from './CommentModal';
import { InfoBanner } from '../InfoBanner/InfoBanner';

type Props = {
  form?: string;
  authenticator?: string;
  canComment: boolean;
  comments: string;
  service: CommentsService;
};

export const Comments = ({
  form,
  authenticator,
  canComment,
  comments,
  service,
}: Props) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [commentHTMLStr, setCommentHTMLStr] = useState<string>('');
  const [isAllowedToComment, setIsAllowedToComment] = useState(true);
  const [confirmButton, setConfirmButton] = useState<HTMLElement>();
  const [showErrorBanner, setShowErrorBanner] = useState(false);

  const setAuthenticator = () => {
    const id = '#form-widgets-comment-text';
    const element = document.querySelector(id);

    const input = (
      <input name="_authenticator" type="hidden" value={authenticator}></input>
    );

    if (element) render(input, element);
  };

  const setCommentForm = () => {
    const confirmButton = document.querySelectorAll(
      '.submit-widget',
    )[0] as HTMLElement;

    // Set custom form footer to enable comment modal open button
    commentForm.setFooter({
      confirmButton,
      stateUpdater: {
        setIsModalOpen,
        setCommentHTMLStr,
        setIsAllowedToComment,
        setConfirmButton,
        setShowErrorBanner,
      },
    });
  };

  const setReplyForm = () => {
    const commentReplyButtons =
      document.querySelectorAll<HTMLElement>('.js-comment-reply');

    // Set flag variable to avoid reply form suplication on replyButton click
    let isFormLoading = false;
    commentReplyButtons.forEach(replyButton => {
      // Add an event listener to each reply button, to load the reply form on click
      replyButton.addEventListener('click', async e => {
        e.preventDefault(); // Prevent following the link directly.

        // Early return if user is not allowed to comment at current time of the day
        if (!isCommentAllowed()) return;

        if (isFormLoading) return;
        isFormLoading = true;

        // Casting to access the href attr
        const target = e.target as HTMLAnchorElement;

        const form = await commentForm.loadReply({
          target,
          replyButton,
          service,
        });

        isFormLoading = false;

        if (!form) return;
        const { confirmButton, commentId, replyForm } = form;

        // Set custom reply form footer to enable comment modal open button
        commentForm.setFooter({
          confirmButton,
          stateUpdater: {
            setIsModalOpen,
            setCommentHTMLStr,
            setIsAllowedToComment,
            setConfirmButton,
            setShowErrorBanner,
          },
          commentId,
          replyForm,
        });
      });
    });
  };

  const allowReplyByTimeOfDay = () => {
    const commentReplies =
      document.querySelectorAll<HTMLElement>('.js-comment-reply');

    commentReplies.forEach(reply => {
      // Initially, if comment is not allowed, every `reply comment` button is disabled
      if (!isCommentAllowed()) {
        reply.classList.add('bo-btn--disabled');
        setIsAllowedToComment(() => isCommentAllowed());
      }

      reply.addEventListener('click', () => {
        // On `reply comment` button click, if comment is not allowed, every `comment` button is disabled
        // and enable showing the reply's error info banner
        if (!isCommentAllowed()) {
          Array.from(commentReplies).map(reply =>
            reply.classList.add('bo-btn--disabled'),
          );

          setShowErrorBanner(() => true);
        }
      });
    });
  };

  useEffect(() => {
    afterLoadingComments();
    setCommentForm();
    setReplyForm();

    if (authenticator) setAuthenticator();

    setIsAllowedToComment(() => isCommentAllowed());
    allowReplyByTimeOfDay();
  }, []);

  const showForm = canComment && (isAllowedToComment || commentHTMLStr);
  const commentsNotAllowed =
    !isAllowedToComment && !commentHTMLStr && !showErrorBanner;
  const showInfoBanner = commentsNotAllowed || showErrorBanner;
  const infoBannerType =
    (commentsNotAllowed && 'lock') || (showErrorBanner && 'error') || 'info';

  useEffect(() => {
    // Remount component if one of the set state changes to be able to show the banners
  }, [isAllowedToComment, showErrorBanner]);

  return (
    <>
      <div className="bc-comment-form__wrapper">
        {showInfoBanner && (
          <InfoBanner
            type={infoBannerType}
            title="Kommentarfunktion geschlossen"
            content={`Die Kommentarfunktion ist derzeit geschlossen. Sie können jeden Tag zwischen ${allowedTime.startTime}:00 und ${allowedTime.endTime}:00 kommentieren.`}
            marginBottom="var(--DF-PRIM-SPACING-24)"
          />
        )}

        {showForm && form && (
          <div id="comment-form-wrapper">
            <form
              className="bc-comment-form"
              dangerouslySetInnerHTML={{ __html: form }}
            ></form>
          </div>
        )}
      </div>

      <div
        className="js-comments"
        dangerouslySetInnerHTML={{ __html: comments }}
      ></div>

      {commentHTMLStr && confirmButton && isModalOpen && (
        <CommentModal
          setIsModalOpen={setIsModalOpen}
          comment={commentHTMLStr}
          confirmButton={confirmButton}
        />
      )}
    </>
  );
};
